import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useAppContext } from '@prisjakt/ui-app-context';

import { H3Text } from '@prisjakt/ui/text/variants';
import {
  VisualNavigation,
  VisualNavigationLink,
} from '@prisjakt/ui/visual-navigation';

import { useEffectOnMount } from 'src/hooks/useEffectOnMount';

import {
  trackSearchResultCategoriesListed,
  trackSearchResultCategoryClicked,
} from '../../tracking';

import { TabAnchor } from '../navigation/types';
import {
  CategoryResults as CategoryResultsType,
  SearchFacetListItem,
  Maybe,
} from 'src/types';
import { FacetLink } from 'src/areas/product-collection/components/navigation/types';

import { Section } from './styles';

import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from 'src/globals/tracking/tracking';

type NewCategories = CategoryResultsType['nodes'] | undefined;
type OldCategories = Maybe<SearchFacetListItem>[];

type Props = {
  visible: boolean;
  active: boolean;
  categories: NewCategories | OldCategories;
  count: number;
  query: string;

  // Experiment flags
  newSearch: boolean;
  wideContent: boolean;
};

export const CategoryResults = (props: Props) => {
  const {
    active,
    categories,
    count,
    newSearch,
    query,
    visible,
    wideContent,
  } = props;

  const { t, market, linkTo } = useAppContext();

  useEffectOnMount(() => {
    if (categories?.length) trackSearchResultCategoriesListed();
  });

  const categoriesFromNewSearch = categories as NewCategories;
  const categoriesFromOldSearch = categories as OldCategories;

  const formattedLinks: FacetLink[] = useMemo(
    () =>
      !newSearch
        ? categoriesFromOldSearch?.map(category => {
            const imageUrl = category?.logo;
            const link: FacetLink = {
              id: category?.id || '',
              value: category?.name || '',
              selected: false,
              path: category?.url,
              imageUrl,
              indexable: true,
            };
            return link;
          }) ?? []
        : categoriesFromNewSearch?.map(category => {
            const link = {
              id: category?.id || '',
              value: category?.name || '',
              selected: false,
              path:
                category?.type === 'classic' && market.useClassicUrls
                  ? linkTo('category', category)
                  : category?.path,
              imageUrl: category?.productMedia || category?.image,
              indexable: true,
            };

            return link;
          }) ?? [],
    [
      categoriesFromNewSearch,
      categoriesFromOldSearch,
      newSearch,
      linkTo,
      market.useClassicUrls,
    ]
  );

  if (!categories?.length) return null;

  return (
    <Section id={TabAnchor.CATEGORIES} $visible={visible}>
      <H3Text>{`${t('search', 'categories.title')} (${count})`}</H3Text>

      <VisualNavigation linksPerRow={wideContent ? 5 : 4} compact={!active}>
        {formattedLinks.map((link, index) => (
          <VisualNavigationLink
            key={link.id}
            label={link.value}
            imageSrc={link.imageUrl || ''}
            imageAlt={link.value}
            containerProps={{
              title: link.value,
            }}
            linkContainer={Link}
            linkContainerProps={{
              to: link.path,
              onClick: () => {
                trackSearchResultCategoryClicked(link.id, link.value, query);
                if (link.path) {
                  trackInternalNavigationClick({
                    scope: InternalNavigationScope.CATEGORY_CARD,
                    nextPage: link.path,
                    index,
                  });
                }
              },
            }}
          />
        ))}
      </VisualNavigation>
    </Section>
  );
};
